import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'
import useAxios from '@/services/api/useAxios'
import type { FormularColumn } from '@/types/formular'
import { afterReceiving, afterReceivingAnswer, afterReceivingSurvey } from './hooks'

const resource = 'surveys'

export type TSurveyResource = {
  _id: string
  firstname: string
  lastname: string
  picture?: string | null
  display?: string
}

export type TSurveyListItem = {
  _id: string
  display: string
  isClosed: boolean
  _created: Date
  authorId?: TSurveyResource | null
  participantCount: number
  completedCount: number
}

export type TSurveyListResponse = {
  docs: TSurveyListItem[]
  count: { total: number, currentPage: number, totalPages: number, hasNextPage: boolean, limit: number }
}

export type TSurveyListQuery = {
  page: number
  limit: number
  search?: string
  isClosed: boolean
}

export type TMySurvey = {
  _id: string
  display: string
  isClosed: boolean
  _created: Date
  authorId?: TSurveyResource | null
  evaluationCompleted: Date | null
}

export type TSurveyParticipant = {
  _id?: string
  resourceId: TSurveyResource
  evaluationCompleted: Date | null
  comment?: string
  form: FormularColumn[]
}

export type TSurvey = {
  _id: string
  display: string
  isClosed: boolean
  _created: Date
  authorId?: TSurveyResource | null
  formularId?: { _id: string, display: string, list: FormularColumn[], globalCommentIsDisabled?: boolean } | null
  participants: TSurveyParticipant[]
}

export type TSurveyAnswer = {
  _id: string
  display: string
  isClosed: boolean
  _created: Date
  authorId?: TSurveyResource | null
  globalCommentIsDisabled: boolean
  participant: {
    resourceId: string
    evaluationCompleted: Date | null
    comment: string
    form: FormularColumn[]
  }
}

export type TSurveyCreatePayload = {
  display: string
  formularId: string
  participantIds: string[]
}

export type TSurveyUpdatePayload = {
  display?: string
  participantIds?: string[]
  isClosed?: boolean
}

const list = (axios: IFactoryFunctionRequest<TSurveyListResponse, undefined>) => (query: TSurveyListQuery): IFactoryResponse<TSurveyListResponse> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    query,
    afterReceivingHook: afterReceiving,
  })
}

const listMine = (axios: IFactoryFunctionRequest<TMySurvey[], undefined>) => (query: { isCompleted: boolean }): IFactoryResponse<TMySurvey[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/mine`,
    query,
    afterReceivingHook: afterReceiving,
  })
}

const getResourcesByJob = (axios: IFactoryFunctionRequest<TSurveyResource[], undefined>) => (jobId: string): IFactoryResponse<TSurveyResource[]> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/resources-by-job/${jobId}`,
  })
}

const getOne = (axios: IFactoryFunctionRequest<TSurvey, undefined>) => (surveyId: string): IFactoryResponse<TSurvey> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${surveyId}`,
    afterReceivingHook: afterReceivingSurvey,
  })
}

const getAnswer = (axios: IFactoryFunctionRequest<TSurveyAnswer, undefined>) => (surveyId: string): IFactoryResponse<TSurveyAnswer> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${surveyId}/answer`,
    afterReceivingHook: afterReceivingAnswer,
  })
}

const create = (axios: IFactoryFunctionRequest<{ _id: string }, TSurveyCreatePayload>) => (data: TSurveyCreatePayload): IFactoryResponse<{ _id: string }> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2`,
    data,
  })
}

const update = (axios: IFactoryFunctionRequest<Pick<TSurvey, '_id' | 'display' | 'isClosed'>, TSurveyUpdatePayload>) => (surveyId: string, data: TSurveyUpdatePayload): IFactoryResponse<Pick<TSurvey, '_id' | 'display' | 'isClosed'>> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${surveyId}`,
    data,
  })
}

const saveAnswer = (axios: IFactoryFunctionRequest<{ _id: string, evaluationCompleted: string | null }, { form: FormularColumn[], comment: string, closeEvaluation: boolean }>) => (surveyId: string, data: { form: FormularColumn[], comment: string, closeEvaluation: boolean }): IFactoryResponse<{ _id: string, evaluationCompleted: string | null }> => {
  return axios({
    method: EMethod.put,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${surveyId}/answer`,
    data,
  })
}

/**
 * Reminds the participants who have not answered yet (all of them without `resourceIds`)
 */
const sendReminders = (axios: IFactoryFunctionRequest<{ sentCount: number }, { resourceIds?: string[] }>) => (surveyId: string, data: { resourceIds?: string[] } = {}): IFactoryResponse<{ sentCount: number }> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/v2/${surveyId}/reminders`,
    data,
  })
}

export default (axios: AxiosInstance) => ({
  list: list(useAxios(axios)),
  listMine: listMine(useAxios(axios)),
  getResourcesByJob: getResourcesByJob(useAxios(axios)),
  getOne: getOne(useAxios(axios)),
  getAnswer: getAnswer(useAxios(axios)),
  create: create(useAxios(axios)),
  update: update(useAxios(axios)),
  saveAnswer: saveAnswer(useAxios(axios)),
  sendReminders: sendReminders(useAxios(axios)),
})
